import React, { type ChangeEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BodyPortal } from '@/components/BodyPortal';
import { EmptyState } from '@/components/EmptyState';
import { ChatMessageCard } from '@/components/HomeTaskModal/ChatMessageCard';
import { MessageComposer } from '@/components/HomeTaskModal/MessageComposer';
import { useSendHomeTaskMessage } from '@/components/HomeTaskModal/hooks/useSendHomeTaskMessage';
import { Paperclip } from '@/components/Icons/Paperclip';
import { Snackbar } from '@/components/Snackbar';
import { useAttachmentUploads } from '@/hooks/useAttachmentUploads';
import type { Attachment } from '@/types/attachment';
import { AttachmentUploadOrigin, SnackbarVariant } from '@/types/components';
import type { HomeTaskMessageThread } from '@/types/diary';
import { ErrorMessage, logRequestError } from '@/utils/errors';
import { warmUpCapturePermission } from '@/utils/permissions';

interface Props {
  isStudent: boolean;
  homeTaskId: number;
  thread?: HomeTaskMessageThread;
  onPreview: (attachment: Attachment) => void;
}

export const MessageThreadSection: React.FC<Props> = ({
  isStudent,
  homeTaskId,
  thread,
  onPreview,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isSendErrorOpen, setIsSendErrorOpen] = useState(false);

  const messages = thread?.messages ?? [];
  const studentId = thread?.student.id;

  const {
    attachments,
    uploadedAttachments,
    isReadyForSending,
    addFiles,
    removeAttachment,
    clearAttachments,
  } = useAttachmentUploads({
    origin: AttachmentUploadOrigin.HomeTask,
  });

  const { mutateAsync: sendMessage, isPending: isSending } =
    useSendHomeTaskMessage(homeTaskId);

  const handleSend = async (text: string) => {
    try {
      await sendMessage({
        text,
        attachmentIds: uploadedAttachments.map((attachment) => attachment.id),
      });
      clearAttachments();

      return true;
    } catch (error) {
      logRequestError(ErrorMessage.SendHomeTaskMessage, error);
      setIsSendErrorOpen(true);

      return false;
    }
  };

  const openFileDialog = async () => {
    await warmUpCapturePermission();
    fileInputRef.current?.click();
  };

  const handleFilesSelected = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      addFiles(Array.from(event.target.files));
    }

    event.target.value = '';
  };

  return (
    <section className="flex flex-1 flex-col gap-3 rounded-t-2xl bg-card pt-4 pb-[calc(16px+env(safe-area-inset-bottom))] shadow-card contrast:border contrast:border-border md:pb-4">
      <div className="flex items-center justify-between gap-3 px-4 md:px-5">
        <h3 className="text-callout-16 min-w-0 truncate text-foreground">
          {t('diary.homeTask.messageThread')}
        </h3>

        {isStudent && (
          <button
            type="button"
            aria-label={t('mail.composeModal.attach')}
            onClick={openFileDialog}
            className="flex size-6 shrink-0 cursor-pointer items-center justify-center text-foreground"
          >
            <Paperclip className="size-5" />
          </button>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        hidden
        onChange={handleFilesSelected}
      />

      {!isStudent && (
        <EmptyState description={t('diary.homeTask.lockedChat')} />
      )}

      {isStudent && (
        <MessageComposer
          attachments={attachments}
          isSending={isSending}
          canSend={isReadyForSending}
          onRemoveAttachment={removeAttachment}
          onSend={handleSend}
        />
      )}

      {isStudent && !!messages.length && (
        <div className="flex flex-col gap-4 pt-3">
          {messages.map((message) => (
            <ChatMessageCard
              key={message.id}
              message={message}
              isOwn={message.author.id === studentId}
              onPreview={onPreview}
            />
          ))}
        </div>
      )}

      <BodyPortal>
        <Snackbar
          isOpen={isSendErrorOpen}
          message={t('global.errorOccurred')}
          variant={SnackbarVariant.Error}
          onDismiss={() => setIsSendErrorOpen(false)}
        />
      </BodyPortal>
    </section>
  );
};
